import React, { useState, useEffect, useContext } from 'react'
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom'

import clsx from 'clsx'
import makeStyles from '@mui/styles/makeStyles'

import CircularProgress from '@mui/material/CircularProgress'

import roadmapGoalWorkspace from './assets/roadmap-goal-workspace.svg'
import yellowVector from './assets/yellow-vector.svg'
import asterisks from './assets/multiple-asterisk.svg'

import { LoginContext } from './app/contexts'
import { useVersion } from './app/hooks'
import { THEME_COLOR_GROUP_NEUTRAL } from './app/support/theme'
import { notifyLoginStatusFromIframe } from './app/utilities'
import { HTTP_FORBIDDEN, HTTP_OK } from './arch/constants'
import { clearPersistedToken, login, user } from './auth'
import { setState } from './common'
import { userDialogStyles } from './commonStyles'
import model from './model'

import LoginFields from './subcomponents/LoginFields'
import Wordmark from './subcomponents/brand/Wordmark'
import BrandButton from './subcomponents/brand/BrandButton'
import UndividedQuotes from './subcomponents/UndividedQuotes'

const useStyles = makeStyles(
  theme => ({
    ...userDialogStyles(theme),

    signupBar: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      margin: theme.spacing(3, 0),
      textAlign: 'center'
    },

    statusBar: {
      flexWrap: 'wrap',
      gap: theme.spacing(1, 2),
      justifyContent: 'space-between',
      padding: theme.spacing(1)
    },

    statusMessage: {
      ...theme.typography.body1
    },

    error: {
      color: theme.palette.error.main
    },

    info: {
      color: theme.palette.info.dark
    },

    submitInProgress: {
      color: theme.palette.common.white,

      '&.Mui-disabled': {
        backgroundColor: THEME_COLOR_GROUP_NEUTRAL.N60
      }
    },

    loginProgress: {
      color: theme.palette.common.white
    },

    linksContainer: {
      justifyContent: 'center'
    },

    links: {
      display: 'flex',
      fontWeight: theme.typography.fontWeightMedium,
      justifyContent: 'space-between',
      width: theme.spacing(30)
    },

    linkUnderline: {
      flexGrow: 1,
      textAlign: 'right',
      textDecoration: 'underline'
    },

    signupButton: {
      padding: theme.spacing(2, 5),
      width: theme.spacing(20.375)
    },

    sidebarContentContainer: {
      padding: theme.spacing(0, 5.75, 9.25, 13.625)
    },

    yellowVector: {
      position: 'absolute',
      right: 0,
      top: 0,
      zIndex: -1
    },

    roadmapImage: {
      height: theme.spacing(55.25),
      marginTop: theme.spacing(4),
      width: '100%'
    },

    quotesContainer: {
      alignItems: 'flex-end',
      display: 'flex',
      position: 'relative'
    },

    asterisks: {
      bottom: theme.spacing(3.75),
      position: 'absolute',
      right: '80%',
      top: theme.spacing(7.25)
    }
  }),
  {
    name: 'Login'
  }
)

const LANDING_DEFAULT = '/home'

const Login = props => {
  const [redirectToReferrer, setRedirectToReferrer] = useState(false)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loginInProgress, setLoginInProgress] = useState(false)
  const [failedLogin, setFailedLogin] = useState(false)
  const [unconfirmedEmail, setUnconfirmedEmail] = useState(false)

  const [queryParams] = useSearchParams()

  const referringPage = queryParams.get('referring_page')
  const referringObject = queryParams.get('referringObject')

  const joinUrlParams = new URLSearchParams()

  joinUrlParams.set('referring_page', referringPage ?? 'loginScreen')

  if (referringObject) {
    joinUrlParams.set('referringObject', referringObject)
  }

  const joinUrl = `/join?${joinUrlParams}`

  const { setAppUser } = useContext(LoginContext)

  const canLogin = () => true // Was (username && password) but hardcoded to true due to Chrome autofill behavior.

  const location = useLocation()
  const { state } = location
  const { from, message, email: priorUsername } = state ?? { from: LANDING_DEFAULT }

  const version = useVersion()

  // A little damp but hey, hooks.
  const setters = {
    username: setUsername,
    password: setPassword
  }

  const handleLoginFieldsChange = changedFields => setState(setters, changedFields)

  const handleSignUpClick = () => notifyLoginStatusFromIframe({ navigate: `${window.location.origin}/app${joinUrl}` })

  const handleLogin = async event => {
    event.preventDefault()

    setLoginInProgress(true)
    setFailedLogin(false)

    try {
      const response = await login(username, password)
      const { status } = response

      const successfulLogin = status === HTTP_OK
      const unconfirmedLogin = status === HTTP_FORBIDDEN

      const inIframe = window.top !== window.self

      // if we are on an iframe Login Modal,this logic helps us prevent the rendering of main app inside the iframe modal which is happening for a second before redirecting back to the main app
      if (inIframe && successfulLogin) {
        notifyLoginStatusFromIframe({ successfulLogin })
      } else {
        setLoginInProgress(false)
        setFailedLogin(!successfulLogin)

        // Unconfirmed login gets a stub user to allow for confirmation email resend.
        setAppUser(unconfirmedLogin ? model.user({ email: username }) : user())

        // These states will trigger navigates so we only set the second one conditionally in order to
        // avoid the “Can’t perform a React state update on an unmounted component” warning.
        setRedirectToReferrer(successfulLogin)

        if (unconfirmedLogin) {
          setUnconfirmedEmail(unconfirmedLogin)
        }
      }
    } catch (error) {
      setLoginInProgress(false)
      setFailedLogin(true)
    }
  }

  useEffect(() => {
    if (!redirectToReferrer) {
      clearPersistedToken()
    }
  }, [redirectToReferrer])

  const classes = useStyles(props)

  if (redirectToReferrer) {
    return <Navigate to={!priorUsername || priorUsername === username ? from : LANDING_DEFAULT} />
  }

  if (unconfirmedEmail) {
    return <Navigate to="/confirm-email" />
  }

  return (
    <form className={classes.root} onSubmit={handleLogin}>
      <article className={classes.dialogContainer}>
        <section className={classes.dialog}>
          <Wordmark classes={{ logoContainer: classes.wordmarkLogoContainer, logo: classes.wordmarkLogo }} />

          <h1 className={classes.prompt}>Welcome back</h1>

          <LoginFields model={{ username, password }} onChange={handleLoginFieldsChange} />

          <section className={clsx(classes.buttonBar, classes.statusBar)}>
            {failedLogin || message ? (
              <span className={clsx(classes.statusMessage, failedLogin ? classes.error : classes.info)}>
                {failedLogin ? 'Sorry, but that didn’t work.' : message}
              </span>
            ) : (
              <span /> /* Keep a placeholder for spacing */
            )}

            <Link className={classes.linkUnderline} to={{ pathname: '/password-reset', state: { from } }}>
              Forgot password?
            </Link>
          </section>

          <section className={classes.buttonBar}>
            <BrandButton
              type="submit"
              variant="contained"
              classes={{
                root: clsx(classes.submit, classes.submitButton),
                contained: loginInProgress ? classes.submitInProgress : undefined,
                label: loginInProgress ? classes.submitInProgress : undefined
              }}
              disabled={loginInProgress || !canLogin()}
              startIcon={loginInProgress && <CircularProgress className={classes.loginProgress} size={18} />}
              fullWidth
            >
              {loginInProgress ? 'Logging in' : 'Login'}
            </BrandButton>
          </section>

          <section className={clsx(classes.signupBar, classes.copy)}>
            Don’t have an account yet?
            <BrandButton
              className={classes.signupButton}
              classes={{ root: classes.buttonColor }}
              variant="outlined"
              component={Link}
              to={joinUrl}
              onClick={handleSignUpClick}
            >
              Sign up
            </BrandButton>
          </section>
        </section>

        <footer className={clsx(classes.footer, classes.linksContainer)}>
          <section className={classes.links}>
            <a
              className={clsx(classes.link, classes.linkUnderline)}
              href="https://undivided.io/terms-of-use"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Use
            </a>

            <a
              className={clsx(classes.link, classes.linkUnderline)}
              href="https://undivided.io/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy
            </a>
          </section>
        </footer>
      </article>

      <aside className={clsx(classes.accent, classes.accentAction)}>
        <img className={classes.yellowVector} src={yellowVector} alt="Mission graphic" />
        <img className={classes.roadmapImage} src={roadmapGoalWorkspace} alt="Mission graphic" />

        <section className={classes.quotesContainer}>
          <img className={classes.asterisks} src={asterisks} alt="Asterisks graphic" />
          <UndividedQuotes
            className={classes.sidebarContentContainer}
            classes={{ sidebarContent: classes.sidebarContent }}
            text="Undivided has been the biggest blessing. My current IEP process has been the most robust so far but it's also been the least stressful.”"
            member="Undivided client"
          />
        </section>

        <footer className={classes.footer}>
          {version.copyright}&nbsp;
          <span>v{version.app}</span> {/* No build tooltip because it messes up snapshot tests. */}
        </footer>
      </aside>
    </form>
  )
}

export default Login
